import { supabase } from '@/utils/supabase';
import type { ArtigoManual } from '@/utils/beaaManual';

/**
 * RAG por significado da Beaa.
 *
 * A busca deixa de olhar só as palavras (título + tags) e passa a comparar o
 * SIGNIFICADO da pergunta com o conteúdo dos artigos, via embeddings (modelo
 * embutido do Supabase, gte-small) e busca vetorial (pgvector).
 *
 * Tudo roda numa Edge Function — o front só envia a pergunta e recebe os
 * artigos mais parecidos. Se algo falhar, a Beaa continua respondendo pela
 * busca por palavra-chave (retrocesso), então nada quebra.
 *
 * O mesmo mecanismo serve dois "ambientes":
 *   - 'console' → manuais internos do Console Administrativo;
 *   - 'portal'  → guia do portal do cidadão.
 */

export type AmbienteManual = 'console' | 'portal';

export interface ArtigoManualBusca extends ArtigoManual {
  similarity: number;
}

/** Pergunta que caiu no "não sei" — usada para priorizar novos artigos. */
export interface PerguntaSemResposta {
  id: string;
  perfil: string;
  topico_id: string;
  pergunta_normalizada: string;
  ocorrencias: number;
  criado_em: string;
  atualizado_em: string;
}

const FUNCAO = 'beaa-manual-rag';

// Garante que a indexação só é pedida uma vez por sessão.
let indiceSolicitado = false;

/**
 * Pede à Edge Function para (re)gerar os embeddings pendentes dos artigos.
 * Roda em pequenos lotes (a função processa poucos por chamada) até não
 * sobrar nada. É idempotente e silenciosa — a Beaa nunca deixa de responder
 * por causa disso.
 */
export async function garantirIndiceManual(): Promise<void> {
  if (indiceSolicitado) return;
  indiceSolicitado = true;
  try {
    // No máximo 12 lotes por sessão (proteção contra laços longos).
    for (let i = 0; i < 12; i += 1) {
      const { data, error } = await supabase.functions.invoke(FUNCAO, {
        body: { action: 'indexar' },
      });
      if (error) {
        indiceSolicitado = false;
        return;
      }
      const faltam = (data as { faltam?: number } | null)?.faltam ?? 0;
      if (faltam <= 0) break;
    }
  } catch {
    indiceSolicitado = false;
  }
}

/**
 * Busca os artigos mais parecidos com a pergunta pelo significado, dentro de
 * um ambiente (console ou portal). Devolve `null` quando não há resultado ou
 * quando a busca não está disponível (o chamador usa o retrocesso por palavra).
 */
export async function buscarManualSemantico(
  pergunta: string,
  matchCount = 4,
  ambiente?: AmbienteManual,
): Promise<ArtigoManualBusca[] | null> {
  const termo = pergunta?.trim();
  if (!termo) return null;

  try {
    const { data, error } = await supabase.functions.invoke(FUNCAO, {
      body: { action: 'buscar', pergunta: termo, matchCount, ambiente: ambiente ?? null },
    });
    if (error) return null;

    const resultados = (data as { resultados?: ArtigoManualBusca[] } | null)?.resultados;
    if (!resultados || resultados.length === 0) return null;
    return resultados;
  } catch {
    return null;
  }
}

/**
 * Registra uma pergunta que a Beaa não soube responder. Serve para priorizar
 * quais novos artigos de manual criar. Silenciosa: nunca atrapalha a conversa.
 */
export async function registrarPerguntaBeaa(
  perfil: string,
  pergunta: string,
  topicoId?: string,
): Promise<void> {
  const termo = pergunta?.trim();
  if (!termo) return;
  try {
    await supabase.functions.invoke(FUNCAO, {
      body: { action: 'registrarPergunta', perfil, topicoId, pergunta: termo },
    });
  } catch {
    /* silencioso de propósito */
  }
}

/** Lista as perguntas que ainda não têm resposta, das mais frequentes às menos. */
export async function listarPerguntasSemResposta(): Promise<PerguntaSemResposta[]> {
  try {
    const { data, error } = await supabase.functions.invoke(FUNCAO, {
      body: { action: 'perguntasSemResposta' },
    });
    if (error) return [];
    return (data as { perguntas?: PerguntaSemResposta[] } | null)?.perguntas ?? [];
  } catch {
    return [];
  }
}